import React, { useRef, useState, useEffect } from 'react';
import './PerformanceAnalytics.css'; // Assuming you have a CSS file for styling
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
	fetchTimePerQuestion,
	fetchStudyTime,
	fetchStudentTestAccuracy,
	fetchMissedQuestionDetails,
	fetchUserActivitySummary,
} from '../../services/UsersApi'; // Adjust the import path as needed
//import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import { get, set, size } from 'lodash';
import KCMasteryHeatmap from './KCMasteryHeatmap';
import MissedQuestionsWordCloud from './MissedQuestionsWordCloud';
import ActivitySummary from './ActivitySummary';
import Loader from '../../components/atoms/Loader.jsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PerformanceAnalytics = () => {
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const [timeData, setTimeData] = useState(null);
	const [studyData, setStudyData] = useState(null);
	const [testAccuracyData, setTestAccuracyData] = useState(null);
	const timeCanvasRef = useRef(null);
	const studyCanvasRef = useRef(null);
	const testAccuracyRef = useRef(null);
	const missedKcRef = useRef(null);
	const { userId } = useSelector((state) => state.userDetails);
	useEffect(() => {
		setIsLoading(true);
		fetchData();
	}, []); // Fetch data only when the component mounts
	const [subtopicMapData, setSubtopicMapData] = useState({});
	const [missedKcs, setMissedKcs] = useState([]);
	const [activitySummary, setActivitySummary] = useState({
		totalKCMastered: 0,
		totalQuestionsAttempted: 0,
		totalStudyTime: 0,
	});

	const fetchData = async () => {
		const res = (await fetchTimeData(userId)).data.data;
		const res1 = (await getStudyTimeInWeek(userId)).data.data;
		if (!timeCanvasRef.current) return;
		if (!studyCanvasRef.current) return;

		const ctx = timeCanvasRef.current.getContext('2d');
		const gradient1a = ctx.createLinearGradient(0, 0, 0, 400);
		gradient1a.addColorStop(0, '#FDE459');
		gradient1a.addColorStop(1, '#FD8058');

		const gradient1b = ctx.createLinearGradient(0, 0, 0, 400);

		gradient1b.addColorStop(0, '#5BDF94');
		gradient1b.addColorStop(1, '#5D75F6');

		setTimeData({
			labels: ['Reading & Writing', 'Math'],
			datasets: [
				{
					label: 'Time Per Question (minutes)',
					data: [res.READING_WRITING, res.MATH], //[5.2, 8.8],
					backgroundColor: [gradient1a, gradient1b],
					borderRadius: 4,
					barThickness: 50,
				},
			],
		});

		const ctx1 = timeCanvasRef.current.getContext('2d');
		const gradient2a = ctx1.createLinearGradient(0, 0, 0, 400);
		gradient2a.addColorStop(1, '#FD8058');
		gradient2a.addColorStop(0, '#FDE459');

		const gradient2b = ctx1.createLinearGradient(0, 0, 0, 400);

		gradient2b.addColorStop(0, '#00F260');
		gradient2b.addColorStop(1, '#1F21E6');

		const gradient2c = ctx1.createLinearGradient(0, 0, 0, 400);

		gradient2c.addColorStop(0, '#2141E5');
		gradient2c.addColorStop(1, '#E83A36');

		setStudyData({
			labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
			datasets: [
				{
					label: 'Study Time (hours)',
					data: [
						res1.Monday,
						res1.Tuesday,
						res1.Wednesday,
						res1.Thursday,
						res1.Friday,
						res1.Saturday,
						res1.Sunday,
					],
					//backgroundColor: gradients,
					//backgroundColor: [gradient1, gradient2],
					backgroundColor: [
						gradient2a,
						gradient2b,
						gradient2c,
						gradient2a,
						gradient2b,
						gradient2c,
						gradient2a,
					],
					borderRadius: 4,
					barThickness: 50,
				},
			],
		});

		getMissedQuestionDetails(userId).then((missedKcsRes) => {
			console.log('Missed KCs Data:', missedKcsRes.data.data);
			const missedKcsData = missedKcsRes.data.data;
			setMissedKcs(missedKcsData);
			const subtopicMap = {};
			missedKcsData.forEach((item) => {
				subtopicMap[item.subtopicId] = item.subtopicName;
			});
			console.log('Subtopic Map:', subtopicMap);
			setSubtopicMapData(subtopicMap);
		});

		getUserActivitySummary(userId).then((userActivityRes) => {
			userActivityRes = userActivityRes.data.data;
			console.log('User Activity Summary:', userActivityRes);
			setActivitySummary({
				totalKCMastered: userActivityRes.totalKCsMastered || 0,
				totalQuestionsAttempted: userActivityRes.totalQuestionsAttempted || 0,
				totalStudyTime: userActivityRes.totalStudyTime || 0,
			});
		});
		getStudentTestAccuracy(userId).then((testAccuracyRes) => {
			let testAccuracyData = testAccuracyRes.data;
			console.log('Student Test Accuracy Data:', testAccuracyData);

			const ctx3 = testAccuracyRef.current.getContext('2d');

			// Define gradients using ctx3
			const gradient3a = ctx3.createLinearGradient(0, 0, 0, 400);
			gradient3a.addColorStop(1, '#1F21E6');
			gradient3a.addColorStop(0, '#00F260');

			const gradient3b = ctx3.createLinearGradient(0, 0, 0, 400);
			gradient3b.addColorStop(1, '#E83A36');
			gradient3b.addColorStop(0, '#F97700');

			const gradient3c = ctx3.createLinearGradient(0, 0, 0, 400);
			gradient3c.addColorStop(1, '#FD8058');
			gradient3c.addColorStop(0, '#FDE459');

			console.log('testAccuracyData', testAccuracyData);
			// Set chart data
			setTestAccuracyData({
				labels: ['Quiz', 'SFT', 'SAT'],
				datasets: [
					{
						label: 'Accuracy per test',
						data: [
							testAccuracyData.accuracyInConceptQuiz,
							testAccuracyData.accuracyInSFT,
							testAccuracyData.accuracyInSAT,
						],
						backgroundColor: [gradient3a, gradient3b, gradient3c],
						borderRadius: 4,
						barThickness: 50,
					},
				],
			});
			setIsLoading(false);
			console.log('End of setTestAccuracyData');
		});
	};

	// useEffect(() => {
	// 	console.log('subtopicMapData: ', subtopicMapData);
	// });

	const handleBackClick = () => {
		navigate('/user-detail');
	};

	const fetchTimeData = async (userId) => {
		console.log('Fetching time data for user ID:', userId);
		const data = await fetchTimePerQuestion(userId);
		console.log('data for fetchTime', data);
		return data;
	};

	const getStudyTimeInWeek = async (userId) => {
		const data = await fetchStudyTime(userId);
		console.log('data for fetchStudyTime', data);
		return data;
	};

	const getStudentTestAccuracy = async (userId) => {
		const data = await fetchStudentTestAccuracy(userId);
		//console.log('data for fetchStudentTestAccuracy', data);
		return data;
	};

	const getMissedQuestionDetails = async (userId) => {
		const data = await fetchMissedQuestionDetails(userId);
		return data;
	};

	const getUserActivitySummary = async (userId) => {
		const data = await fetchUserActivitySummary(userId);
		return data;
	};

	const hoursOptions = {
		responsive: true,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (ctx) => `${ctx.parsed.y} hrs`,
				},
			},
		},
		scales: {
			x: {
				grid: {
					drawOnChartArea: false, // Disable full grid lines
					drawTicks: true,
					drawBorder: true,
					color: '#fff', // Only the X-axis line remains
				},
				ticks: {
					color: '#fff',
					font: { weight: 'bold', size: 14 },
				},
				border: {
					display: true,
					color: '#fff',
					width: 4,
				},
			},
			y: {
				beginAtZero: true,
				grid: {
					drawOnChartArea: false, //  Disable full grid lines
					drawTicks: true,
					drawBorder: true,
					color: '#fff', // Only the Y-axis line remains
				},
				ticks: {
					color: '#fff',
					font: { weight: 'bold', size: 14 },
					callback: (val) => `${val} hrs`,
				},
				border: {
					display: true,
					color: '#fff',
					width: 4,
				},
			},
		},
	};

	const percentageOptions = {
		responsive: true,
		plugins: {
			legend: { display: false },

			tooltip: {
				callbacks: {
					label: (ctx) => `${ctx.parsed.y}%`,
				},
			},
		},
		scales: {
			x: {
				title: {
					display: true,
					text: 'Exam Types',
					color: '#fff',
					font: {
						weight: 'bold',
						size: 16,
					},
				},
				grid: {
					drawOnChartArea: false, // Disable full grid lines
					drawTicks: true,
					drawBorder: true,
					color: '#fff', // Only the X-axis line remains
				},
				ticks: {
					color: '#fff',
					font: { weight: 'bold', size: 14 },
				},
				border: {
					display: true,
					color: '#fff',
					width: 4,
				},
			},
			y: {
				beginAtZero: true,
				title: {
					display: true,
					text: 'Percentage',
					color: '#fff',
					font: {
						weight: 'bold',
						size: 16,
					},
				},
				grid: {
					drawOnChartArea: false, //  Disable full grid lines
					drawTicks: true,
					drawBorder: true,
					color: '#fff', // Only the Y-axis line remains
				},
				ticks: {
					color: '#fff',
					font: { weight: 'bold', size: 14 },
					callback: (val) => `${val}%`,
				},
				border: {
					display: true,
					color: '#fff',
					width: 4,
				},
			},
		},
	};

	return (
		<div className="dashboard-container">
			<div className="glow-background" />

			{/* <button className="back-button" onClick={() => handleBackClick()}>
				<span className="back-symbol">←</span> Back
			</button> */}
			{/* Desktop Back Button */}
			<button className="back-button desktop-only" onClick={handleBackClick}>
				<span className="back-symbol">←</span> Back
			</button>

			{/* Mobile Back Header */}
			<div className="mobile-header mobile-only" onClick={handleBackClick}>
				<div className="header-title-container">
					<span className="back-arrow">←</span>
					<span className="header-title">Performance Analytics</span>
				</div>
			</div>

			<h2 className="title1">Score & Time Trend</h2>
			<div className="charts-row">
				<div className="chart-card">
					<h3 className="chart-title">Time Per Question (minutes)</h3>
					<canvas ref={timeCanvasRef} style={{ display: 'none' }} />
					{timeData && <Bar data={timeData} options={hoursOptions} />}
				</div>
				<div className="chart-card">
					<h3 className="chart-title">Cumulative Time by Week</h3>
					<canvas ref={studyCanvasRef} style={{ display: 'none' }} />
					{studyData && <Bar data={studyData} options={hoursOptions} />}
				</div>
			</div>
			<h2 className="title1">KC Mastery Heatmap</h2>
			<div className="charts-row">
				<div className="card-chart-one">
					<KCMasteryHeatmap />
				</div>
			</div>
			<h2 className="title1">Quiz/Test Breakdown</h2>
			<div className="charts-row">
				<div className="chart-card">
					<h3 className="chart-title">Accuracy per Test</h3>
					<canvas ref={testAccuracyRef} />
					{isLoading ? (
						<Loader />
					) : (
						testAccuracyData && (
							<Bar data={testAccuracyData} options={percentageOptions} />
						)
					)}
				</div>

				<div className="chart-card">
					<h3 className="chart-title">Missed question types</h3>
					<canvas ref={missedKcRef} style={{ display: 'none' }} />
					<MissedQuestionsWordCloud
						missedKcs={missedKcs}
						subtopicIdToNameMap={subtopicMapData}
					/>
				</div>
			</div>
			<h2 className="title1">Activity Summary</h2>
			<div className="charts-row">
				<div className="card-chart-one">
					<ActivitySummary activitySummary={activitySummary} />
				</div>
			</div>
		</div>
	);
};

export default PerformanceAnalytics;
